import React from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Paper,
  Typography,
  Stack,
} from '@mui/material';
import {
  Search as SearchIcon,
  Tag as ServiceIcon,
  Phone as PhoneIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';

export interface CustomerQuickSearchValues {
  serviceNumber: string;
  mobileNumber: string;
  generalSearch?: string;
}

interface CustomerQuickSearchProps {
  serviceNumber: string;
  mobileNumber: string;
  onServiceNumberChange: (value: string) => void;
  onMobileNumberChange: (value: string) => void;
  onClear?: () => void;
  generalSearch?: string;
  onGeneralSearchChange?: (value: string) => void;
  generalSearchPlaceholder?: string;
  showGeneralSearch?: boolean;
  title?: string;
  embedded?: boolean;
}

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    bgcolor: 'background.paper',
  },
};

const CustomerQuickSearch: React.FC<CustomerQuickSearchProps> = ({
  serviceNumber,
  mobileNumber,
  onServiceNumberChange,
  onMobileNumberChange,
  onClear,
  generalSearch = '',
  onGeneralSearchChange,
  generalSearchPlaceholder = 'Search by name, VIN, policy...',
  showGeneralSearch = false,
  title = 'Quick Search',
  embedded = false,
}) => {
  const hasValues = Boolean(
    serviceNumber.trim() || mobileNumber.trim() || (showGeneralSearch && generalSearch.trim())
  );

  const content = (
    <>
      {title && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <SearchIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" fontWeight={600} color="text.primary">
              {title}
            </Typography>
          </Stack>
          {hasValues && onClear && (
            <IconButton size="small" onClick={onClear} aria-label="Clear search">
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      )}

      {!title && hasValues && onClear && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
          <IconButton size="small" onClick={onClear} aria-label="Clear search">
            <ClearIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: showGeneralSearch ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)',
          },
          gap: 1.5,
        }}
      >
        <TextField
          size="small"
          label="Service Number"
          placeholder="e.g. SRV-12345"
          value={serviceNumber}
          onChange={(e) => onServiceNumberChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <ServiceIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={fieldSx}
        />
        <TextField
          size="small"
          label="Customer Mobile"
          placeholder="e.g. 96812345678"
          value={mobileNumber}
          onChange={(e) => onMobileNumberChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PhoneIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={fieldSx}
        />
        {showGeneralSearch && onGeneralSearchChange && (
          <TextField
            size="small"
            label="General Search"
            placeholder={generalSearchPlaceholder}
            value={generalSearch}
            onChange={(e) => onGeneralSearchChange(e.target.value)}
            sx={fieldSx}
          />
        )}
      </Box>
    </>
  );

  if (embedded) {
    return <Box sx={{ mb: 1 }}>{content}</Box>;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'grey.50',
      }}
    >
      {content}
    </Paper>
  );
};

export default CustomerQuickSearch;
